import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, FolderOpen, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { qmsPlanService, clientService, productService, productRevisionService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATO_BADGE = {
  Attivo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Obsoleto: 'bg-slate-200 text-slate-600 border-slate-300',
};

export default function QmsPlansPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'quality');

  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [productRevisions, setProductRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ customer_code: 'all', product_code: '', revision: '', stato: 'all' });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    customer_code: '', product_code: '', revision: '', id_configurazione: '', titolo: '', note: '',
  });

  useEffect(() => { load(); loadRefs(); }, []);
  useEffect(() => { load(); }, [filters]);

  // Auto-load revisions when product changes in create form
  useEffect(() => {
    if (!form.product_code) { setProductRevisions([]); return; }
    const product = products.find(p => p.code === form.product_code);
    if (!product) { setProductRevisions([]); return; }
    productRevisionService.getAll({ product_id: product.id }).then(r => {
      const revs = r.data || [];
      setProductRevisions(revs);
      // Auto-pick the active revision (or first available)
      if (revs.length && !form.revision) {
        const active = revs.find(rev => rev.is_active) || revs[0];
        setForm(f => ({ ...f, revision: active.revision_code }));
      }
    }).catch(() => setProductRevisions([]));
  }, [form.product_code, products, form.revision]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.customer_code && filters.customer_code !== 'all') params.customer_code = filters.customer_code;
      if (filters.product_code) params.product_code = filters.product_code;
      if (filters.revision) params.revision = filters.revision;
      if (filters.stato && filters.stato !== 'all') params.stato = filters.stato;
      const r = await qmsPlanService.list(params);
      setPlans(r.data || []);
    } finally { setLoading(false); }
  };

  const loadRefs = async () => {
    try {
      const [cl, pr] = await Promise.all([clientService.getAll(), productService.getAll()]);
      setClients(cl.data || []);
      setProducts(pr.data || []);
    } catch (e) { /* non-blocking */ }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await qmsPlanService.create({
        customer_code: form.customer_code,
        product_code: form.product_code,
        revision: form.revision,
        id_configurazione: form.id_configurazione || null,
        titolo: form.titolo || null,
        note: form.note || null,
      });
      toast.success('Piano creato');
      setCreateOpen(false);
      setForm({ customer_code: '', product_code: '', revision: '', id_configurazione: '', titolo: '', note: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione');
    }
  };

  return (
    <div className="space-y-6 pb-12" data-testid="qms-plans-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-slate-700" />
            Piano Controlli Prodotto
          </h1>
          <p className="text-sm text-slate-500 mt-1">Master configurazione QMS — caratteristiche da controllare per Cliente/Prodotto/Revisione.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setCreateOpen(true)} data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-1" />Nuovo Piano
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={filters.customer_code} onValueChange={v => setFilters({ ...filters, customer_code: v })}>
                <SelectTrigger data-testid="filter-customer"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Codice Prodotto</Label>
              <Input value={filters.product_code} onChange={e => setFilters({ ...filters, product_code: e.target.value })} placeholder="es. PROD001" data-testid="filter-product" />
            </div>
            <div>
              <Label className="text-xs">Revisione</Label>
              <Input value={filters.revision} onChange={e => setFilters({ ...filters, revision: e.target.value })} placeholder="es. 01" data-testid="filter-revision" />
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={filters.stato} onValueChange={v => setFilters({ ...filters, stato: v })}>
                <SelectTrigger data-testid="filter-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="Attivo">Attivo</SelectItem>
                  <SelectItem value="Obsoleto">Obsoleto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Piani ({plans.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Caricamento...</p>
          ) : plans.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessun piano</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-2 pr-4">Cliente</th>
                    <th className="py-2 pr-4">Prodotto</th>
                    <th className="py-2 pr-4">Rev.</th>
                    <th className="py-2 pr-4">Titolo</th>
                    <th className="py-2 pr-4">Righe</th>
                    <th className="py-2 pr-4">Stato</th>
                    <th className="py-2 pr-4">Aggiornato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(p => (
                    <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`plan-row-${p.id}`}>
                      <td className="py-2 pr-4">
                        <p className="font-mono text-xs">{p.customer_code}</p>
                        <p className="text-xs text-slate-500">{p.customer_name}</p>
                      </td>
                      <td className="py-2 pr-4">
                        <p className="font-mono text-xs">{p.product_code}</p>
                        <p className="text-xs text-slate-500 truncate max-w-xs">{p.product_description}</p>
                      </td>
                      <td className="py-2 pr-4 font-mono">{p.revision}</td>
                      <td className="py-2 pr-4">{p.titolo || <span className="text-slate-400">—</span>}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{p.active_rows_count}/{p.total_rows_count}</td>
                      <td className="py-2 pr-4"><Badge className={`${STATO_BADGE[p.stato_piano]} border`}>{p.stato_piano}</Badge></td>
                      <td className="py-2 pr-4 text-xs text-slate-500">{p.updated_at ? new Date(p.updated_at).toLocaleDateString('it-IT') : '—'}</td>
                      <td className="py-2 pr-4">
                        <Link to={`/qms-plans/${p.id}`}>
                          <Button size="sm" variant="outline" data-testid={`open-plan-${p.id}`}>
                            <FolderOpen className="w-3 h-3 mr-1" />Apri
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Piano Controlli</DialogTitle>
            <DialogDescription>Cliente + Prodotto devono esistere in anagrafica ERP.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={form.customer_code} onValueChange={v => setForm({ ...form, customer_code: v, product_code: '', revision: '' })}>
                <SelectTrigger data-testid="form-customer"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prodotto *</Label>
              <Select value={form.product_code} onValueChange={v => setForm({ ...form, product_code: v, revision: '' })}>
                <SelectTrigger data-testid="form-product"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                <SelectContent>
                  {products.filter(p => !form.customer_code || p.client_id === clients.find(c => c.code === form.customer_code)?.id)
                    .map(p => <SelectItem key={p.id} value={p.code}>{p.code} · {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Revisione *</Label>
                <Select value={form.revision} onValueChange={v => setForm({ ...form, revision: v })}>
                  <SelectTrigger data-testid="form-revision"><SelectValue placeholder={form.product_code ? 'Seleziona' : 'Scegli prodotto'} /></SelectTrigger>
                  <SelectContent>
                    {productRevisions.length === 0 && <SelectItem value="__empty" disabled>Nessuna revisione</SelectItem>}
                    {productRevisions.map(r => <SelectItem key={r.id} value={r.revision_code}>{r.revision_code}{r.is_active ? ' · attiva' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ID Configurazione</Label>
                <Input value={form.id_configurazione} onChange={e => setForm({ ...form, id_configurazione: e.target.value })} placeholder="opz." />
              </div>
            </div>
            <div>
              <Label>Titolo</Label>
              <Input value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} placeholder="opz." />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="form-submit">Crea Piano</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
